/**
 * V3 mockup-aligned project header: customer identity, inline related contacts, summary tiles.
 * Contact editing stays in the dashboard contacts modal.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CaretDown,
    CheckCircle,
    Copy,
    MapPin,
    MapTrifold,
    UsersThree,
} from '@phosphor-icons/react';
import { notification } from 'antd';
import { useObdGoogleStreetViewActions } from '../ObdGoogleStreetViewProvider';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import opportunityInfoService from '../../../../../services/oneButtonServices/opportunityInfoService';
import type { OpportunitySalesPerson } from '../../../../../@types/oneButtonType';
import {
    contactHasPassedCredit,
    getFinancePartnerCreditStatusList,
    getOpportunityPrimaryFinancePartner,
    getPrimaryContactRecord,
    opportunityHasSunlightPartner,
    resolveContactCreditVisualStatus,
    type ContactCreditVisualStatus,
} from '../contactCreditStatus';
import SalesPersonModal from '../SalesPersonModal';
import { TitleInfoModal } from '../TitleInfoModal';
import { CreditStatusModal } from '../CreditStatusModal';
import {
    getContactSnapshotTags,
    getUtilityHolderSnapshotTags,
    type ContactSnapshotTag,
} from '../contactSnapshotTags';
import {
    opportunityHasSyncedTitleContact,
    syncedTitleContactName,
} from '../../../../../helpers/titleContactStatus';
import {
    contactRecordCommaName,
    contactRecordFriendlyName,
    contactRecordInitials,
    customerInitials,
    formatAddress,
    formatPhone,
    primaryContactFriendlyName,
} from './projectHeaderUtils';

export type DashboardV3ProjectHeaderHeroProps = {
    theme: 'dark' | 'light';
    opportunityName: string;
    opportunityId: string;
    siteAddress?: string | null;
    utilityCompany: string;
    loading?: boolean;
    isTitleContactExist?: boolean | null;
    onAddTitleContactsClick?: () => void;
    onBlackKnightTitleCheckClick?: () => void;
};

type RelatedContactRow = {
    key: string;
    initials: string;
    name: string;
    tags: ContactSnapshotTag[];
    fallbackRole?: string;
    creditPassed: boolean;
    credit?: { status: Exclude<ContactCreditVisualStatus, 'none'>; label: string };
    detail: React.ReactNode;
};

const CREDIT_CHIP_LABEL: Record<Exclude<ContactCreditVisualStatus, 'none'>, string> = {
    passed: 'Credit passed',
    pending: 'Credit pending',
    declined: 'Credit rejected',
};

const CREDIT_TILE_VALUE: Record<Exclude<ContactCreditVisualStatus, 'none'>, string> = {
    passed: 'Passed',
    pending: 'Pending',
    declined: 'Rejected',
};

function formatPartnerProductLine(partner: string, product: string): string {
    if (partner && product) return `${partner} · ${product}`;
    return partner || product;
}

function creditTilePartnerProductLines(
    contact: Record<string, unknown> | null,
    opportunityDetails: unknown,
): string[] {
    const seen = new Set<string>();
    const lines: string[] = [];
    const addLine = (partner: string, product: string) => {
        const line = formatPartnerProductLine(partner, product);
        const key = line.toLowerCase();
        if (!line || seen.has(key)) return;
        seen.add(key);
        lines.push(line);
    };

    if (contact) {
        for (const row of getFinancePartnerCreditStatusList(contact)) {
            addLine(
                String(row.Finance_Partner_Name ?? row.finance_Partner_Name ?? '').trim(),
                String(row.productTypeName ?? row.ProductTypeName ?? '').trim(),
            );
        }
    }
    if (lines.length > 0) return lines;

    const details = opportunityDetails && typeof opportunityDetails === 'object'
        ? (opportunityDetails as Record<string, unknown>)
        : null;
    const products = (details?.OpportunityProduct ?? details?.opportunityProduct ?? []) as unknown[];
    if (!Array.isArray(products)) return lines;

    for (const raw of products) {
        if (!raw || typeof raw !== 'object') continue;
        const row = raw as Record<string, unknown>;
        if (row.ActiveFlag === false || row.activeFlag === false) continue;
        if(row.Finance_Partner_Display_Name !== 'Cash'){
            addLine(
                String(
                    row.Finance_Partner_Display_Name ??
                        row.Finance_Partner ??
                        row.finance_Partner_Display_Name ??
                        '',
                ).trim(),
                String(row.Product_Type_Name ?? row.product_Type_Name ?? row.ProductType ?? '').trim(),
            );
        }
    }

    return lines;
}

function CreditPassedFlag({ onClick }: { onClick?: () => void }) {
    const content = (
        <>
            <CheckCircle size={12} weight="fill" aria-hidden />
            Credit Passed
        </>
    );
    if (onClick) {
        return (
            <button
                type="button"
                className="obd-credit-passed-flag"
                onClick={onClick}
                title="View credit status"
                aria-label="Credit Passed"
            >
                {content}
            </button>
        );
    }
    return <span className="obd-credit-passed-flag" aria-label="Credit Passed">{content}</span>;
}

export const DashboardV3ProjectHeaderHero: React.FC<DashboardV3ProjectHeaderHeroProps> = ({
    theme,
    opportunityName,
    opportunityId,
    siteAddress,
    utilityCompany,
    loading = false,
    isTitleContactExist = null,
    onAddTitleContactsClick,
    onBlackKnightTitleCheckClick,
}) => {
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const titleInfo = useOneButtonStore((s) => s.titleInfo);
    const setDashboardContactsModalOpen = useOneButtonStore((s) => s.setDashboardContactsModalOpen);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);

    const [contactsExpanded, setContactsExpanded] = useState(false);
    const [moreDetailsExpanded, setMoreDetailsExpanded] = useState(false);
    const [salesPersonModalOpen, setSalesPersonModalOpen] = useState(false);
    const [titleInfoModalOpen, setTitleInfoModalOpen] = useState(false);
    const [creditStatusModalOpen, setCreditStatusModalOpen] = useState(false);
    const [sunlightCreditStatus, setSunlightCreditStatus] = useState<unknown>(null);
    const [utilityCompanyName, setUtilityCompanyName] = useState<string | null>(null);

    const { openFromRoot, loading: streetViewLoading } = useObdGoogleStreetViewActions();

    const highlight = opportunityDetails?.OpportunityHighlight ?? {};
    const ahj = String((highlight as { AHJ?: string }).AHJ ?? '').trim();
    const managingOffice = String((highlight as { Managing_Office?: string }).Managing_Office ?? '').trim();
    const leadSource =
        opportunityDetails?.OpportunityDetails?.LeadSource ??
        opportunityDetails?.LeadSource ??
        opportunityDetails?.leadSource ??
        '—';

    const salesPerson = opportunityDetails?.SalesPerson as OpportunitySalesPerson | undefined;
    const salesPersonName =
        salesPerson?.Name?.trim() ||
        salesPerson?.name?.trim() ||
        sessionStorage.getItem('salesPersonName')?.trim() ||
        '—';

    const serviceAddress = formatAddress(opportunityDetails, siteAddress);
    const displayName = useMemo(() => {
        if (loading) return '…';
        return primaryContactFriendlyName(opportunityDetails, opportunityName?.trim() || opportunityId || 'Opportunity');
    }, [loading, opportunityDetails, opportunityName, opportunityId]);

    const initials = useMemo(() => customerInitials(displayName), [displayName]);
    const primaryContactRecord = useMemo(
        () => getPrimaryContactRecord(opportunityDetails),
        [opportunityDetails],
    );

    const isSunlightPartnerExist = useMemo(
        () => opportunityHasSunlightPartner(opportunityDetails),
        [opportunityDetails],
    );

    const primaryFinancePartner = useMemo(
        () => getOpportunityPrimaryFinancePartner(opportunityDetails),
        [opportunityDetails],
    );

    const primaryCreditPassed = useMemo(
        () =>
            contactHasPassedCredit(primaryContactRecord, true, {
                sunlightCreditStatus,
                isSunlightPartnerExist,
                primaryFinancePartner,
            }),
        [primaryContactRecord, sunlightCreditStatus, isSunlightPartnerExist, primaryFinancePartner],
    );

    const titleContactSynced = opportunityHasSyncedTitleContact(opportunityDetails);
    const titleContactLabel = syncedTitleContactName(opportunityDetails);
    const showTitleContactsTile = titleContactSynced || isTitleContactExist !== null;

    const resolvedUtility =
        (utilityCompanyName?.trim() ?? utilityCompany?.trim()) || '—';

    const primaryCreditStatus = useMemo(
        () =>
            resolveContactCreditVisualStatus(primaryContactRecord, true, {
                sunlightCreditStatus,
                isSunlightPartnerExist,
                primaryFinancePartner,
            }),
        [primaryContactRecord, sunlightCreditStatus, isSunlightPartnerExist, primaryFinancePartner],
    );

    const showCreditTile = !!(
        primaryContactRecord &&
        (primaryContactRecord.CreditCheckStatus === true ||
            primaryContactRecord.creditCheckStatus === true ||
            primaryCreditStatus !== 'none')
    );

    const creditPartnerProductLines = useMemo(
        () => creditTilePartnerProductLines(primaryContactRecord, opportunityDetails),
        [primaryContactRecord, opportunityDetails],
    );

    useEffect(() => {
        const oppId = opportunityDetails?.Opportunity_Id ?? opportunityId;
        if (!oppId || !isSunlightPartnerExist) {
            setSunlightCreditStatus(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await opportunityInfoService.getSunlightCreditStatus(oppId);
                if (!cancelled) setSunlightCreditStatus(res?.length ? res[0] : null);
            } catch {
                if (!cancelled) setSunlightCreditStatus(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [opportunityDetails?.Opportunity_Id, opportunityId, isSunlightPartnerExist]);

    useEffect(() => {
        setUtilityCompanyName(solarUtilityData?.SolarUtility?.UtilityCompanyName ?? null);
    }, [solarUtilityData]);

    const relatedContactRows = useMemo((): RelatedContactRow[] => {
        const customers = opportunityDetails?.Customers;
        const source: unknown[] = Array.isArray(customers) && customers.length
            ? customers
            : primaryContactRecord
              ? [primaryContactRecord]
              : [];
        const records = source.filter(
            (c: unknown): c is Record<string, unknown> => !!c && typeof c === 'object',
        );
        // When nobody is flagged as the utility-bill holder, the primary contact stands in for that role.
        const hasFlaggedUtilityHolder = records.some((c) => c.IsUtilityBillOwner || c.isUtilityBillOwner);

        const seen = new Set<string>();
        const rows: RelatedContactRow[] = [];

        records.forEach((contact, index) => {
            const first = String(contact.FirstName ?? contact.firstName ?? '').trim();
            const last = String(contact.LastName ?? contact.lastName ?? '').trim();
            const email = String(contact.Email ?? contact.email ?? '').trim();
            const phone = formatPhone(String(contact.Phone ?? contact.phone ?? ''));
            if (!first && !last && !email) return;

            // Salesforce repeats the same person across roles (credit-application name, utility holder),
            // so collapse by identity — a single-contact opportunity should render a single row.
            const identity = `${first}|${last}|${email}`.toLowerCase();
            if (seen.has(identity)) return;
            seen.add(identity);

            const isPrimary =
                contact === primaryContactRecord || !!(contact.IsPrimaryContact || contact.isPrimaryContact);
            const onUtility = !!(contact.IsUtilityBillOwner || contact.isUtilityBillOwner) || (!hasFlaggedUtilityHolder && isPrimary);

            const tags = getContactSnapshotTags(contact);
            if (onUtility && !tags.some((t) => t.key === 'utility')) {
                tags.push(...getUtilityHolderSnapshotTags(contact));
            }

            const creditStatus = resolveContactCreditVisualStatus(contact, isPrimary, {
                sunlightCreditStatus,
                isSunlightPartnerExist,
                primaryFinancePartner,
            });
            const creditPassed = contactHasPassedCredit(contact, isPrimary, {
                sunlightCreditStatus,
                isSunlightPartnerExist,
                primaryFinancePartner,
            });

            const homeownerLine =
                contact.IsHomeowner || contact.isHomeowner || contact.IsHomeOwner || contact.isHomeOwner
                    ? 'Homeowner'
                    : '';

            rows.push({
                key: String(contact.Id ?? contact.id ?? `${identity}-${index}`),
                initials: contactRecordInitials(first, last),
                name: contactRecordFriendlyName(first, last),
                tags,
                creditPassed,
                credit:
                    creditStatus === 'none'
                        ? undefined
                        : { status: creditStatus, label: CREDIT_CHIP_LABEL[creditStatus] },
                detail: (
                    <span className="obd-project-header-hero__contact-detail">
                        {homeownerLine ? (
                            <span className="obd-project-header-hero__contact-detail-line">{homeownerLine}</span>
                        ) : null}
                        {(phone !== '—' || email) && (
                            <span className="obd-project-header-hero__contact-detail-meta">
                                {phone !== '—' ? <span>{phone}</span> : null}
                                {email ? (
                                    <a className="obd-project-header-hero__contact-email" href={`mailto:${email}`}>
                                        {email}
                                    </a>
                                ) : null}
                            </span>
                        )}
                    </span>
                ),
            });
        });

        return rows;
    }, [
        opportunityDetails,
        primaryContactRecord,
        sunlightCreditStatus,
        isSunlightPartnerExist,
        primaryFinancePartner,
    ]);

    const handleCopyId = useCallback(async () => {
        const id = opportunityId?.trim();
        if (!id) return;
        try {
            await navigator.clipboard.writeText(id);
            notification.success({
                message: 'Copied opportunity ID',
                placement: 'topRight',
                duration: 2,
            });
        } catch {
            notification.error({
                message: 'Could not copy ID',
                placement: 'topRight',
                duration: 2,
            });
        }
    }, [opportunityId]);

    const handleToggleContacts = useCallback(() => {
        setContactsExpanded((open) => !open);
    }, []);

    const handleOpenContactsModal = useCallback(() => {
        setDashboardContactsModalOpen(true);
    }, [setDashboardContactsModalOpen]);

    const handleToggleMoreDetails = useCallback(() => {
        setMoreDetailsExpanded((open) => !open);
    }, []);

    const handleOpenStreetView = useCallback(() => {
        void openFromRoot(opportunityDetails);
    }, [openFromRoot, opportunityDetails]);

    const handleOpenCreditStatus = useCallback(() => {
        setCreditStatusModalOpen(true);
    }, []);

    const titlePending =
        titleInfo == null
            ? false
            : Array.isArray(titleInfo)
              ? titleInfo.length === 0
              : !titleInfo;

    return (
        <>
            <header
                className={`obd-dashboard-v3-project-header obd-dashboard-v3-project-header--hero obd-theme-${theme}`}
            >
                {/* Top identity + actions */}
                <div className="obd-project-header-hero__top">
                    <div className="obd-project-header-hero__identity">
                        <span className="obd-project-header-hero__avatar" aria-hidden>
                            {initials}
                        </span>
                        <div className="obd-project-header-hero__identity-body">
                            <div className="obd-project-header-hero__title-row">
                                <h1 className="obd-dashboard-v3-project-header__title">{displayName}</h1>
                                {primaryCreditPassed ? (
                                    <CreditPassedFlag onClick={handleOpenCreditStatus} />
                                ) : null}
                            </div>
                            <p className="obd-project-header-hero__address">
                                <MapPin size={14} weight="duotone" aria-hidden />
                                <span>{serviceAddress}</span>
                                {titlePending ? (
                                    <span className="obd-project-header-hero__flag">Title pending</span>
                                ) : null}
                            </p>
                        </div>
                    </div>

                    <div className="obd-dashboard-v3-project-header__actions" role="group" aria-label="Project actions">
                        <button
                            type="button"
                            className="obd-dashboard-v3-project-header__action"
                            onClick={handleOpenStreetView}
                            disabled={streetViewLoading}
                        >
                            <MapTrifold size={15} weight="duotone" aria-hidden />
                            Street View
                        </button>
                        <button
                            type="button"
                            className={`obd-dashboard-v3-project-header__action${
                                contactsExpanded ? ' obd-dashboard-v3-project-header__action--active' : ''
                            }`}
                            onClick={handleToggleContacts}
                            aria-expanded={contactsExpanded}
                        >
                            <UsersThree size={15} weight="duotone" aria-hidden />
                            Contacts
                        </button>
                        <button
                            type="button"
                            className="obd-dashboard-v3-project-header__action"
                            onClick={handleCopyId}
                            disabled={!opportunityId?.trim()}
                            title={opportunityId ? `Copy ${opportunityId}` : undefined}
                        >
                            <Copy size={15} weight="duotone" aria-hidden />
                            ID
                        </button>
                    </div>
                </div>

                {/* Inline related contacts */}
                {contactsExpanded ? (
                    <section
                        className="obd-project-header-hero__contacts"
                        aria-label="Related contacts"
                    >
                        <div className="obd-project-header-hero__contacts-head">
                            <div className="obd-project-header-hero__contacts-title">
                                <UsersThree size={16} weight="duotone" aria-hidden />
                                <span>
                                    Related contacts
                                    <span className="obd-project-header-hero__contacts-sub"> · {displayName}</span>
                                </span>
                            </div>
                            <div className="obd-project-header-hero__contacts-head-actions">
                                <button
                                    type="button"
                                    className="obd-project-header-hero__manage-link"
                                    onClick={handleOpenContactsModal}
                                >
                                    Manage contacts
                                </button>
                                {opportunityId ? (
                                    <span className="obd-project-header-hero__contacts-id">{opportunityId}</span>
                                ) : null}
                            </div>
                        </div>

                        <ul className="obd-project-header-hero__contact-list" role="list">
                            {relatedContactRows.map((row) => (
                                <li key={row.key} className="obd-project-header-hero__contact-row" role="listitem">
                                    <div className="obd-project-header-hero__contact-main">
                                        <span className="obd-project-header-hero__contact-avatar" aria-hidden>
                                            {row.initials}
                                        </span>
                                        <div className="obd-project-header-hero__contact-copy">
                                            <div className="obd-project-header-hero__contact-name-line">
                                                <span className="obd-project-header-hero__contact-name">{row.name}</span>
                                                {row.creditPassed ? <CreditPassedFlag /> : null}
                                            </div>
                                            {row.tags.length > 0 ? (
                                                <div
                                                    className="obd-contact-row__tags-row obd-project-header-hero__contact-tags"
                                                    role="list"
                                                    aria-label="Contact roles"
                                                >
                                                    {row.tags.map((tag) => (
                                                        <span
                                                            key={tag.key}
                                                            className={`obd-contact-row__tag obd-contact-row__tag--${tag.tone}`}
                                                            role="listitem"
                                                        >
                                                            {tag.label}
                                                        </span>
                                                    ))}
                                                    {!row.creditPassed && row.credit ? (
                                                        <span
                                                            className={`obd-project-header-hero__contact-credit obd-project-header-hero__contact-credit--${row.credit.status}`}
                                                            role="listitem"
                                                        >
                                                            {row.credit.label}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : row.fallbackRole ? (
                                                <span className="obd-project-header-hero__contact-role">
                                                    {row.fallbackRole}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="obd-project-header-hero__contact-aside">{row.detail}</div>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                {/* Summary tiles */}
                <div className="obd-project-header-hero__summary">
                    <div className="obd-project-header-hero__summary-tiles">
                        <div className="obd-project-header-hero__summary-tile">
                            <span className="obd-project-header-hero__summary-label">Utility</span>
                            <span className="obd-project-header-hero__summary-value">{resolvedUtility}</span>
                        </div>
                        <button
                            type="button"
                            className="obd-project-header-hero__summary-tile obd-project-header-hero__summary-tile--link"
                            onClick={() => setSalesPersonModalOpen(true)}
                        >
                            <span className="obd-project-header-hero__summary-label">Sales</span>
                            <span className="obd-project-header-hero__summary-value">{salesPersonName}</span>
                        </button>
                        <div className="obd-project-header-hero__summary-tile">
                            <span className="obd-project-header-hero__summary-label">Office</span>
                            <span className="obd-project-header-hero__summary-value">{managingOffice || '—'}</span>
                        </div>
                        {showCreditTile ? (
                            <button
                                type="button"
                                className="obd-project-header-hero__summary-tile obd-project-header-hero__summary-tile--link"
                                onClick={() => setCreditStatusModalOpen(true)}
                            >
                                <span className="obd-project-header-hero__summary-label">Credit</span>
                                <span className="obd-project-header-hero__summary-value">
                                    {primaryCreditStatus !== 'none'
                                        ? CREDIT_TILE_VALUE[primaryCreditStatus]
                                        : 'View credit status'}
                                </span>
                                {creditPartnerProductLines.length > 0 ? (
                                    <span className="obd-project-header-hero__summary-meta">
                                        {creditPartnerProductLines.slice(0, 3).join('\n')}
                                        {creditPartnerProductLines.length > 3
                                            ? `\n+${creditPartnerProductLines.length - 3} more`
                                            : ''}
                                    </span>
                                ) : null}
                            </button>
                        ) : null}
                        {showTitleContactsTile ? (
                            <button
                                type="button"
                                className="obd-project-header-hero__summary-tile obd-project-header-hero__summary-tile--link"
                                onClick={
                                    titleContactSynced
                                        ? () => setTitleInfoModalOpen(true)
                                        : isTitleContactExist === true
                                          ? onAddTitleContactsClick
                                          : onBlackKnightTitleCheckClick
                                }
                            >
                                <span className="obd-project-header-hero__summary-label">Title contacts</span>
                                <span className="obd-project-header-hero__summary-value">
                                    {titleContactSynced
                                        ? titleContactLabel || 'On title'
                                        : 'Add title contacts'}
                                </span>
                            </button>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className={`obd-project-header-hero__more-btn${
                            moreDetailsExpanded ? ' obd-project-header-hero__more-btn--open' : ''
                        }`}
                        onClick={handleToggleMoreDetails}
                        aria-expanded={moreDetailsExpanded}
                    >
                        More details
                        <CaretDown size={14} weight="bold" aria-hidden />
                    </button>
                </div>

                {/* Expanded details */}
                {moreDetailsExpanded ? (
                    <div className="obd-project-header-hero__more-panel">
                        <ul className="obd-project-header-hero__more-grid" role="list">
                            {leadSource && leadSource !== '—' ? (
                                <li className="obd-project-header-hero__more-item" role="listitem">
                                    <span className="obd-project-header-hero__more-label">Lead source</span>
                                    <span className="obd-project-header-hero__more-value">{leadSource}</span>
                                </li>
                            ) : null}
                            <li className="obd-project-header-hero__more-item" role="listitem">
                                <span className="obd-project-header-hero__more-label">AHJ</span>
                                <span className="obd-project-header-hero__more-value">{ahj || '—'}</span>
                            </li>
                            <li className="obd-project-header-hero__more-item" role="listitem">
                                <span className="obd-project-header-hero__more-label">Primary contact</span>
                                <button
                                    type="button"
                                    className="obd-dashboard-v3-project-header__meta-link"
                                    onClick={handleOpenContactsModal}
                                >
                                    {primaryContactRecord
                                        ? contactRecordCommaName(
                                              String(primaryContactRecord.FirstName ?? primaryContactRecord.firstName ?? ''),
                                              String(primaryContactRecord.LastName ?? primaryContactRecord.lastName ?? ''),
                                          )
                                        : '—'}
                                </button>
                            </li>
                            <li className="obd-project-header-hero__more-item" role="listitem">
                                <span className="obd-project-header-hero__more-label">Title</span>
                                <button
                                    type="button"
                                    className="obd-dashboard-v3-project-header__meta-link"
                                    onClick={() => setTitleInfoModalOpen(true)}
                                >
                                    View title info
                                </button>
                            </li>
                        </ul>
                    </div>
                ) : null}
            </header>

            <SalesPersonModal
                open={salesPersonModalOpen}
                onClose={() => setSalesPersonModalOpen(false)}
                salesPerson={salesPerson}
            />
            <TitleInfoModal
                open={titleInfoModalOpen}
                onClose={() => setTitleInfoModalOpen(false)}
                raw={titleInfo}
                theme={theme}
            />
            <CreditStatusModal
                open={creditStatusModalOpen}
                onClose={() => setCreditStatusModalOpen(false)}
                contact={primaryContactRecord}
                primaryFinancePartner={primaryFinancePartner}
            />
        </>
    );
};
